/**
 * Conversations — `GET /threads?project_id=…` and one thread's transcript,
 * `GET /threads/{id}/chat/history`. Ported from `bimgo-ai-web`'s `useChat`,
 * which talks to these two endpoints directly.
 *
 * Both answer the `{ success, data }` envelope, unlike the members endpoints
 * next door, which answer bare. That inconsistency is api-v2's, and absorbing it
 * here is the reason this module exists.
 *
 * Two things about the API that the UI has to respect:
 *
 * 1. **`project_id` is required on the list.** There is no "all my threads"
 *    call — a thread belongs to a project, and the server 422s without one. The
 *    drawer is therefore only ever openable with a project selected, which is
 *    also how the web client has it.
 * 2. **Threads are scoped to the caller, not the project.** The list is
 *    `user_id AND project_id`, so two people in the same project do not see each
 *    other's conversations. The history call is owner-only and answers **404**,
 *    not 403, for someone else's thread — so a 404 there means "gone *or* not
 *    yours", and must not be reported as "deleted".
 *
 * A **403 on the list** is its own thing: it means the caller lost access to the
 * project since it was selected. That is worth saying plainly rather than
 * rendering an empty list, which reads as "no conversations yet".
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";

export interface Thread {
  /** The API renames `id` to `thread_id` on the way out. */
  thread_id: string;
  title: string | null;
  project_id: string | null;
  file_id: string | null;
  created_at: string | null;
}

export interface ThreadMessage {
  id: number;
  thread_id: string;
  /** `"user"` or `"ai"` — the web client keys its bubbles off exactly this. */
  sender: string;
  content: string | null;
  images: string[] | null;
  timestamp: string | null;
}

async function get(
  url: string,
  token: string | null,
  fetchImpl: typeof fetch,
): Promise<unknown> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  let response: Response;
  try {
    response = await fetchImpl(url, { headers: { Authorization: bearer(token) } });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  // 401 alone is "not signed in". 403 is *not* folded in here the way it is for
  // organizations: on this endpoint it means the project access went away, which
  // is a different sentence to the user and needs its status preserved.
  if (response.status === 401) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return (body as { data?: unknown } | null)?.data;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, "");
}

/**
 * The caller's conversations in one project, newest first.
 *
 * The server does not promise an order, and the drawer is a history list — the
 * most recent conversation is the one being looked for, so it is sorted here
 * rather than left to whatever the query returned.
 */
export async function getThreads(
  token: string | null,
  projectId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<Thread[]> {
  const url = `${base(deps.apiBaseUrl)}/threads?project_id=${encodeURIComponent(projectId)}`;
  const data = await get(url, token, deps.fetchImpl ?? fetch);
  if (!Array.isArray(data)) return [];
  return (data as Thread[]).slice().sort(byNewest);
}

/** Newest first; a thread with no `created_at` sorts last rather than first. */
function byNewest(a: Thread, b: Thread): number {
  const at = a.created_at ? Date.parse(a.created_at) : NaN;
  const bt = b.created_at ? Date.parse(b.created_at) : NaN;
  if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
  if (Number.isNaN(at)) return 1;
  if (Number.isNaN(bt)) return -1;
  return bt - at;
}

/** One thread's messages, oldest first — the order they are read in. */
export async function getThreadMessages(
  token: string | null,
  threadId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<ThreadMessage[]> {
  const url = `${base(deps.apiBaseUrl)}/threads/${encodeURIComponent(threadId)}/chat/history`;
  const data = await get(url, token, deps.fetchImpl ?? fetch);
  // Sorted here, once, so no caller has to know about the timestamp tie.
  return Array.isArray(data) ? sortThreadMessages(data as ThreadMessage[]) : [];
}

/** The HTTP status of a failure, or null when it failed some other way. */
export function statusOf(error: unknown): number | null {
  return error instanceof OrganizationsError && error.failure.kind === "http"
    ? error.failure.status
    : null;
}

/**
 * Does opening this thread need its transcript fetched?
 *
 * **Yes, unless the transcript on screen already belongs to it.** That is the
 * whole rule, and it is narrower than the one it replaces.
 *
 * The old rule skipped the fetch for any thread *minted in this session*. It
 * existed for a real reason: the agent hands back a `thread_id` mid-stream on the
 * first turn of a new conversation, so `activeThreadId` moves from null to a real
 * id while an answer is streaming — and fetching then replaces a live answer with
 * an empty list. But "was minted" is a property of the session, not of the
 * moment, so it kept skipping forever:
 *
 *   1. start a conversation — the agent mints thread A;
 *   2. open thread B from History — fetched, B is on screen;
 *   3. open thread A again — skipped, **and B's messages stay on screen**.
 *
 * Keying on what is actually displayed covers the mid-stream adoption (the
 * transcript is already A's) and nothing else, so step 3 fetches.
 */
export function shouldLoadHistory(threadId: string | null, shownThreadId: string | null): boolean {
  if (!threadId) return false;
  return threadId !== shownThreadId;
}

/**
 * Put a thread's messages in the order they were said.
 *
 * **The server's order is not enough, and the reason is in its schema.**
 * `messages.timestamp` defaults to `func.now()`, which in PostgreSQL is the
 * *transaction* start time — constant for the whole request. A turn files its
 * question and its answer through **one** `POST /messages`, so the two rows are
 * written in one transaction and carry **identical** timestamps. api-v2 then
 * sorts by `timestamp.asc()` alone (`app/services/message.py`), and a tie leaves
 * the order undefined: the answer can come back above the question it answers.
 *
 * `id` is an autoincrement integer, so it is the true insertion order and the
 * only reliable tiebreak. Timestamp first anyway, because ids are per-table and
 * a future backfill could insert rows out of order.
 *
 * Sorting here rather than asking for a server fix: this client cannot change
 * api-v2, the data needed is already in the response, and a transcript that
 * reads backwards is not worth waiting on a deploy for.
 */
export function sortThreadMessages(messages: readonly ThreadMessage[]): ThreadMessage[] {
  return [...messages].sort((a, b) => {
    const at = a.timestamp ? Date.parse(a.timestamp) : Number.NaN;
    const bt = b.timestamp ? Date.parse(b.timestamp) : Number.NaN;
    // An unparseable or missing timestamp must not drag a message to one end:
    // fall through to the id, which is always there and always ordered.
    if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
    return a.id - b.id;
  });
}
